import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { seriesColor } from '../constants/chartPalette';
import {
  ChartAxisLabels,
  ChartDataPoint,
  ChartGrid,
  ChartLegend,
  ChartValueAxis,
  enterAt,
  formatValue,
  niceRange,
  WidgetNothingToDraw,
} from '../shared';

/**
 * Floor for the plot area. The plot itself flexes to whatever height the slot
 * gives it — a chart in a tall widget should draw a tall chart, not the same
 * 96px strip with air underneath — and this is the height below which the
 * bars stop being comparable at a glance.
 */
const PLOT_MIN_PX = 96;

/**
 * Relationships and outliers — one dot per record.
 *
 * `x` and `y` are percentages of the plot, 0-100, with `y` measured from the
 * bottom the way a reader expects an axis to run; the widget inverts it. A
 * caller with raw values normalises first, for the same reason the line chart
 * does not rescale: two renders of a self-scaling scatter cannot be compared.
 *
 * A dot's *position* is the datum, and position alone cannot be read off a
 * plot — without the tooltip a reader sees that the cloud slopes down and
 * cannot name a single point in it. `xLabel` and `yLabel` are what make that
 * tooltip a sentence rather than a coordinate pair.
 *
 * @param {object} props
 * @param {{ points: { x: number, y: number, group?: number, label?: string }[], groups?: string[], axis?: string[], xLabel?: string, yLabel?: string }} props.data
 * @param {string} [props.accent] Overrides the first group's colour.
 * @param {string} [props.className]
 */
/*
  The contract sends real coordinates (CT-5641).

  `CORRELATION` publishes `points[{x, y, size?, label?, series?}]` in the data's
  own units — 1200 visits against 34.000 in revenue — plus the two readings a
  scatter plot exists for and this widget could not show:

  - **`trendline`** `{slope, intercept}`, the line fitted server-side. Drawing
    it is the difference between a cloud of dots and a statement about them.
  - **`coefficient`**, how strong that relationship is. Shown as a figure
    because «the dots look correlated» is not a reading.

  Both are the backend's: `display.trendline` decides whether to fit one at
  all, and a widget fitting its own would disagree with the author's setting.

  Points are projected onto the 0-100 plot here, from a round range *around*
  the data's extent, so a caller no longer has to pre-normalise — which was
  never possible once the numbers came from a query.

  Both axes are labelled, and neither starts at zero. A scatter is read as a
  shape, and a shape pushed into the top corner by an origin nobody asked for
  is not the shape the data has. See `niceRange`.

  A scatter without axes is the one chart where that is fatal: a bar chart
  without a y axis still says which bar is tallest, but a cloud of dots with
  no units does not say *between what and what*.
*/
function CorrelationChartWidget({ data, accent, className = '' }) {
  const points = useMemo(() => data?.points ?? [], [data]);
  const xLabel = data?.xLabel ?? 'x';
  const yLabel = data?.yLabel ?? 'y';

  // Series names, in order of first appearance. A pre-v2 payload numbered its
  // groups; v2 names them on the point.
  const groups = useMemo(() => {
    if (data?.groups) return data.groups;
    return [...new Set(points.map((point) => point.series).filter(Boolean))];
  }, [data, points]);

  /** Round ranges around the data, so the plot fills itself and the axes read. */
  const extent = useMemo(() => {
    const xs = points.map((point) => point.x).filter((n) => typeof n === 'number');
    const ys = points.map((point) => point.y).filter((n) => typeof n === 'number');
    if (!xs.length || !ys.length) return null;
    return {
      x: niceRange(Math.min(...xs), Math.max(...xs)),
      y: niceRange(Math.min(...ys), Math.max(...ys)),
    };
  }, [points]);

  const project = (value, range) => ((value - range.lo) / (range.hi - range.lo || 1)) * 100;

  // No points, or points carrying a series name and no coordinates — which
  // is what a correlation over two null measures answers. Either way there
  // is no cloud to draw, and a blank card does not say so.
  if (!points.length || !extent) return <WidgetNothingToDraw />;

  /* The pre-v2 caller passed its own x labels; v2 derives them from the range. */
  const xLabels = data?.axis ?? extent.x.ticks.map((tick) => formatValue(tick, data?.xFormat));
  const trend = data?.trendline;
  // Two ends of the fitted line, projected the same way the points are.
  const trendEnds = trend && typeof trend.slope === 'number'
    ? [extent.x.lo, extent.x.hi].map((x) => ({
      x: project(x, extent.x),
      y: project(trend.slope * x + (trend.intercept ?? 0), extent.y),
    }))
    : null;

  return (
    <div className={`flex h-full min-h-0 flex-col ${className}`.trim()}>
      <ChartLegend
        items={groups.map((label, index) => ({ label, color: seriesColor(index, accent) }))}
      />

      {/*
        Two columns: the value axis sizes itself to its widest tick and the
        plot takes the rest. The x labels sit in the plot's column so they
        stay under the marks rather than under the gutter.
      */}
      <div
        className="grid min-h-0 flex-1 gap-x-1.5"
        style={{ gridTemplateColumns: 'auto minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr) auto' }}
      >
        <ChartValueAxis ticks={extent.y.ticks} format={data?.yFormat} />

        <div className="relative min-h-0" style={{ minHeight: PLOT_MIN_PX }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0"
          aria-hidden
        >
          <ChartGrid rows={extent.y.ticks.length - 1} />
          {/* The fitted line, under the points so it never hides one. */}
          {trendEnds ? (
            <line
              x1={trendEnds[0].x}
              y1={100 - trendEnds[0].y}
              x2={trendEnds[1].x}
              y2={100 - trendEnds[1].y}
              stroke={seriesColor(0, accent)}
              strokeWidth="1.5"
              strokeDasharray="4 3"
              vectorEffect="non-scaling-stroke"
              opacity="0.7"
            />
          ) : null}
        </svg>

        <div className="absolute inset-0">
          {points.map((point, index) => {
            const group = point.group ?? Math.max(0, groups.indexOf(point.series));
            const enter = enterAt(index, 'scale');
            const name = point.label ?? groups[group] ?? '';
            // `size` scales the dot when the author bound a third measure.
            const diameter = typeof point.size === 'number' ? 7 + Math.min(9, point.size) : 7;

            return (
              <span
                key={`${point.x}-${point.y}-${index}`}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${project(point.x, extent.x)}%`,
                  top: `${100 - project(point.y, extent.y)}%`,
                }}
              >
                <ChartDataPoint
                  label={`${name} · ${xLabel} ${formatValue(point.x)} · ${yLabel} ${formatValue(point.y)}`.trim()}
                >
                  <span
                    className={`block rounded-full ${enter.className}`}
                    style={{
                      ...enter.style,
                      width: diameter,
                      height: diameter,
                      background: seriesColor(group, accent),
                    }}
                  />
                </ChartDataPoint>
              </span>
            );
          })}
        </div>
        </div>

        {/* Row 2, column 2: under the plot, never under the axis gutter. */}
        <div style={{ gridColumn: 2 }}>
          <ChartAxisLabels labels={xLabels} />
        </div>
      </div>

      {/* How strong the relationship is — the reading, not the picture. */}
      {typeof data?.coefficient === 'number' ? (
        <span className="mt-1 text-[11px] text-content-muted">
          {`r = ${data.coefficient.toFixed(2)}`}
        </span>
      ) : null}
    </div>
  );
}

CorrelationChartWidget.propTypes = {
  /** `CORRELATION`'s `data`, as `output.schema.json` publishes it. */
  data: PropTypes.shape({
    points: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      size: PropTypes.number,
      label: PropTypes.string,
      series: PropTypes.string,
      record_uuid: PropTypes.string,
      group: PropTypes.number,
    })).isRequired,
    trendline: PropTypes.shape({ slope: PropTypes.number, intercept: PropTypes.number }),
    coefficient: PropTypes.number,
    // pre-v2
    groups: PropTypes.arrayOf(PropTypes.string),
    axis: PropTypes.arrayOf(PropTypes.string),
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    /** How each axis' ticks print — the contract has no `y_axes` for this type. */
    xFormat: PropTypes.object,
    yFormat: PropTypes.object,
  }).isRequired,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(CorrelationChartWidget);
